// Per-purpose and submission-level error message helpers for the OneTrust consent capture UI.

// Why the form couldn't be sent, from the visitor's point of view. Each maps to a localized
// message SalesforceConsentElementBlock.cshtml renders onto the touchpoint marker as
// data-ssocc-message-{kind}. The messages are fixed texts: never the SDK/API's own error message,
// which isn't written for visitors (or localized) and may describe internals.
export type ConsentErrorKind = "connection" | "email" | "phone" | "save" | "load";

// Used if the marker doesn't carry a localized message (shouldn't happen outside tests).
const FALLBACK_MESSAGES: Record<ConsentErrorKind, string> = {
  connection:
    "The form hasn't been sent because the consent service couldn't be reached. Check your internet connection and try again.",
  email:
    "The form hasn't been sent because the email address wasn't accepted. Check the email address and try again.",
  phone:
    "The form hasn't been sent because the phone number wasn't accepted. Check the phone number and try again.",
  save: "The form hasn't been sent because your consent choices couldn't be saved. Please try again in a moment.",
  load: "The form can't be sent because the consent options didn't load. Reload the page and try again. If that doesn't help, a browser extension such as an ad blocker may be blocking them."
};

const MESSAGE_DATASET_KEYS: Record<ConsentErrorKind, string> = {
  connection: "ssoccMessageConnection",
  email: "ssoccMessageEmail",
  phone: "ssoccMessagePhone",
  save: "ssoccMessageSave",
  load: "ssoccMessageLoad"
};

export const getConsentErrorMessage = (
  marker: HTMLElement,
  kind: ConsentErrorKind
): string => marker.dataset[MESSAGE_DATASET_KEYS[kind]] || FALLBACK_MESSAGES[kind];

export const setPurposeErrorVisible = (
  purposeElement: HTMLElement,
  visible: boolean,
  message?: string
): void => {
  const error = purposeElement.querySelector<HTMLElement>("[data-ssocc-error]");

  if (error) {
    if (visible && message) {
      error.textContent = message;
    }

    error.classList.toggle("hidden", !visible);
  }
};

// Submission-level error (as opposed to the per-purpose errors above) shown when the OneTrust
// consent capture call itself fails or times out - see submitOneTrustConsentCapture.
const getSubmissionErrorElement = (marker: HTMLElement): HTMLElement => {
  let error = marker.querySelector<HTMLElement>("[data-ssocc-submission-error]");

  if (!error) {
    error = document.createElement("p");
    error.className = "hidden text-t-sm font-t-semi-bold text-error-200 mt-sm";
    error.setAttribute("data-ssocc-submission-error", "");
    error.setAttribute("role", "alert");
    marker.append(error);
  }

  return error;
};

export const setSubmissionErrorVisible = (
  marker: HTMLElement,
  visible: boolean,
  message?: string
): void => {
  const error = getSubmissionErrorElement(marker);

  if (visible && message) {
    error.textContent = message;
  }

  error.classList.toggle("hidden", !visible);
};

// Clears any validator message Forms is still displaying for the OneTrust consent element (e.g.
// OneTrustContactRequiredValidator's "email or phone required" text) for the element containing
// `marker`. onBeforeSubmit runs *before* Forms' own validation pass, so if we abort a submit
// attempt ourselves (consent capture failure/timeout) without reaching that pass, a message from
// an earlier, since-corrected submit attempt is never cleared by Forms and is left showing
// alongside our own submission error - misleadingly implying the fields are still invalid. Safe
// to call unconditionally here since this only runs once our own guards have already confirmed
// the OneTrust-relevant fields are valid.
export const clearOneTrustValidatorMessage = (marker: HTMLElement): void => {
  const elementWrapper = marker.closest<HTMLElement>("[data-f-element-name]");
  const fieldName = elementWrapper?.dataset.fElementName;

  if (!fieldName) {
    return;
  }

  const validationError = elementWrapper?.querySelector<HTMLElement>(
    `[data-f-validationerror][data-f-linked-name="${fieldName}"]`
  );

  if (validationError) {
    validationError.textContent = "";
  }
};
